import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  IconButton,
  Popover,
  RadioGroup,
  Typography,
} from '@mui/material';
import KeyboardOutlinedIcon from '@mui/icons-material/KeyboardOutlined';
import TooltipMUI6 from 'src/components/TooltipMUI6';
import TextFieldFormik from 'src/components/TextFieldFormik';
import { STYLE_OPTIONS } from 'src/constants';
import { getEnterKeyHeading, getEnterKeyOptions, getEnterKeyTouchNote } from './enterKeyPreference';

// EnterKeySettings
//
// The keyboard button beside Send and its popover. Lives outside the message
// field and the form, so changing the option never touches the draft; focus
// goes back to the field when the popover closes.
function EnterKeySettings({
  enterKey,
  isMac = false,
  onChange = () => { },
  onClosed = () => { },
}) {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClose = () => {
    setAnchorEl(null);
    // MUI hands focus back to the button after the popover closes, so the
    // message box takes it after that (same delay as the emoji picker).
    setTimeout(onClosed, 100);
  };

  return (
    <>
      <TooltipMUI6 title="Choose what the Enter key does">
        <IconButton
          size="small"
          aria-label="Enter key setting"
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{
            color: anchorEl ? STYLE_OPTIONS.SVG_COLOR_BLUE : '#B5B5B5',
            '&:hover svg': STYLE_OPTIONS.SVG_COLOR_BLUE,
          }}
        >
          <KeyboardOutlinedIcon />
        </IconButton>
      </TooltipMUI6>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Box sx={{ padding: '16px', maxWidth: '360px' }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {getEnterKeyHeading(isMac)}
          </Typography>
          <RadioGroup
            value={enterKey}
            onChange={(event) => onChange(event.target.value)}
            sx={{
              // The radio sits on the main line, not centered between the two.
              '& .MuiFormControlLabel-root': { alignItems: 'flex-start', marginBottom: '8px' },
              '& .MuiRadio-root': { padding: '0 9px' },
            }}
          >
            {getEnterKeyOptions(isMac).map((option) => (
              <TextFieldFormik
                key={option.value}
                fieldType="radio"
                value={option.value}
                formControlLabel={(
                  <Box>
                    <Typography variant="body2">{option.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.shortcut}
                    </Typography>
                  </Box>
                )}
                handleChange={() => { }}
                handleBlur={() => { }}
                size="small"
              />
            ))}
          </RadioGroup>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 1 }}
          >
            {getEnterKeyTouchNote(isMac)}
          </Typography>
        </Box>
      </Popover>
    </>
  );
}

EnterKeySettings.propTypes = {
  enterKey: PropTypes.string.isRequired,
  isMac: PropTypes.bool,
  onChange: PropTypes.func,
  onClosed: PropTypes.func,
};

export default EnterKeySettings;
